import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthRequest } from '../shared/models/auth.request';
import { User } from '../shared/models/user';
import { jwtDecode } from 'jwt-decode';
import { backendRestApis } from '@environments/backend-rest-apis';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;
  private inMemoryStore: { user?: any; accessToken?: string } = {};
  private refreshTimeoutId: any = null;

  // When user explicitly logs out we set this flag to prevent silent refresh from
  // restoring the session in the same browser tab.
  private manualLogout = false;

  constructor(private http: HttpClient) {
    // start with no authenticated user in memory
    this.currentUserSubject = new BehaviorSubject(null);
    this.currentUser = this.currentUserSubject.asObservable();

    // restore manualLogout flag from session storage so a user-initiated logout
    // survives a full page reload (prevents silent refresh from re-authenticating)
    try {
      this.manualLogout = sessionStorage.getItem('manualLogout') === '1';
    } catch {
      this.manualLogout = false;
    }
  }

  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  public get accessToken(): string | undefined {
    return this.currentUserValue?.accessToken;
  }

  // NOTE: refresh token is stored in HttpOnly cookie set by the backend. Client should not access it.

  login(email: string, password: string): Observable<AuthRequest> {
    return this.http
      .post<AuthRequest>(
        `${environment.apiUrl}${backendRestApis.auth.login}`,
        { email, password },
        { withCredentials: true },
      )
      .pipe(
        map((res) => {
          // res.accessToken is expected; refresh token is set in HttpOnly cookie by backend.
          // Store only in memory. Derive user from token if backend doesn't return user object.
          const accessToken = res.accessToken;
          let user: any = (res as any).user;

          if (!user && accessToken) {
            user = this.decodeUserFromToken(accessToken);
          }

          this.inMemoryStore = { user, accessToken };
          this.currentUserSubject.next({ user, accessToken });

          // clear manual-logout flag when user logs in
          this.manualLogout = false;
          try {
            sessionStorage.removeItem('manualLogout');
          } catch {}

          // schedule proactive refresh
          this.scheduleRefresh(accessToken);

          return res;
        }),
        catchError((error) => throwError(() => error)),
      );
  }

  refreshToken(): Observable<AuthRequest> {
    // if user intentionally logged out in this tab, don't attempt to silently refresh
    if (this.manualLogout) {
      return throwError(() => new Error('Manual logout - skipping refresh'));
    }

    // backend should read refresh token from HttpOnly cookie and return new access token
    return this.http
      .post<AuthRequest>(
        `${environment.apiUrl}${backendRestApis.auth.refreshToken}`,
        {},
        { withCredentials: true },
      )
      .pipe(
        tap((res) => {
          const accessToken = res.accessToken;
          const user =
            (res as any).user ||
            this.decodeUserFromToken(accessToken) ||
            this.inMemoryStore.user;

          this.inMemoryStore = { user, accessToken };
          this.currentUserSubject.next({ user, accessToken });

          // clear manual-logout flag on successful refresh
          this.manualLogout = false;
          try {
            sessionStorage.removeItem('manualLogout');
          } catch {}

          // schedule proactive refresh after we received new token
          this.scheduleRefresh(accessToken);
        }),
        catchError((error) => throwError(() => error)),
      );
  }

  register(userData: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}${backendRestApis.auth.register}`, userData);
  }

  logout(): void {
    try {
      // attempt to inform backend to clear the HttpOnly refresh cookie; subscribe so the request is sent
      this.http
        .post(
          `${environment.apiUrl}${backendRestApis.auth.logout}`,
          {},
          { withCredentials: true },
        )
        .subscribe({
          next: () => {
            // backend cleared cookie (best-effort)
          },
          error: () => {
            // ignore errors; we still clear client state below
          },
        });
    } catch {
      // swallow errors from the call attempt
    }

    // mark manual logout so we don't silently refresh in this tab or after reload
    this.manualLogout = true;
    try {
      sessionStorage.setItem('manualLogout', '1');
    } catch {}

    // clear local state immediately so the UI is updated even if the backend call fails
    this.inMemoryStore = {};
    this.currentUserSubject.next(null);
    this.clearScheduledRefresh();

    return;
  }

  private scheduleRefresh(token?: string) {
    try {
      this.clearScheduledRefresh();

      const t = token || this.inMemoryStore.accessToken;
      if (!t) return;

      const expDate = this.getTokenExpirationDate(t);
      if (!expDate) return;

      const now = Date.now();

      // refresh offset from environment (seconds)
      const offsetSeconds =
        (environment as any).tokenRefreshOffsetSeconds || 60;
      const msBeforeExpiry =
        expDate.valueOf() - now - offsetSeconds * 1000;

      if (msBeforeExpiry <= 0) {
        // token already near expiry — refresh immediately
        this.refreshToken().subscribe({
          next: () => {},
          error: () => {
            this.logout();
          },
        });
      } else {
        this.refreshTimeoutId = setTimeout(() => {
          this.refreshToken().subscribe({
            next: () => {},
            error: () => {
              this.logout();
            },
          });
        }, msBeforeExpiry);
      }
    } catch {
      // swallow scheduling errors
    }
  }

  private clearScheduledRefresh() {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }

  isTokenExpired(token?: string): boolean {
    if (!token) token = this.accessToken;
    if (!token) return true;

    const date = this.getTokenExpirationDate(token);
    if (date === undefined) return false;

    return !(date.valueOf() > new Date().valueOf());
  }

  private decodeUserFromToken(token: string): any | undefined {
    try {
      const decoded: any = jwtDecode(token);

      const user = {
        id: decoded.sub,
        username:
          decoded.preferred_username ||
          decoded.username ||
          decoded.sub,
        email: decoded.email,
        roles:
          decoded.roles ||
          decoded.authorities ||
          decoded.role ||
          [],
      };

      return user;
    } catch {
      return undefined;
    }
  }

  private getTokenExpirationDate(token: string): Date | undefined {
    try {
      // Decode JWT payload without external dependency
      const parts = token.split('.');
      if (parts.length !== 3) return undefined;

      const payload = parts[1];

      // base64url decode
      const base64 = payload
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map(
            (c) =>
              '%' +
              ('00' + c.charCodeAt(0).toString(16)).slice(-2),
          )
          .join(''),
      );
      const decoded = JSON.parse(json);

      if (decoded.exp === undefined) return undefined;

      const date = new Date(0);
      date.setUTCSeconds(decoded.exp);

      return date;
    } catch (Error) {
      return undefined;
    }
  }
}
